import { DndContext, MouseSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { flexRender } from "@tanstack/react-table"
import { TableHeader, TableRow, TableHead, cn, Checkbox } from 'twyne-new.ui'

import ColumnHeaderMenu, { SortToggle } from '@Components/ColumnsConfig/HeaderMenu'
import { columnKey, isColumnConfigurable, resolveColumnOrder, useGrid, useGridBulkActions } from '@Components/Grid/context'
import useGridColumnActions from '@Components/Grid/hooks/columnActions'
import { useTableResizeContext } from '@Components/tableResize/context';
import { useColumnWidth } from '@Components/tableResize/hooks';

import { useTableContext } from "./context"
import { usePinnedColumns, usePinnedHeaderCell } from './pinned'

export default function DefaultTableHeader({ onResize }) {
  const table = useTableContext()
  const headerGroups = table.getHeaderGroups()
  const { headerRowRef } = usePinnedColumns()
  const { sensors, handleDragEnd } = useHeaderReorder()

  return <TableHeader sticky>
    {headerGroups.map(({ id, headers }, groupIndex) => <DndContext
      key={id}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={headers.map(({ column }) => columnKey(column.columnDef))} strategy={horizontalListSortingStrategy}>
        <TableRow ref={groupIndex === 0 ? headerRowRef : undefined}>
          <HeaderCheckbox table={table} />
          {headers.map(({ id, column, isPlaceholder, getContext }, headerIndex) => <Column
            key={id}
            headerIndex={headerIndex}
            isPlaceholder={isPlaceholder}
            column={column}
            columnDef={column.columnDef}
            canSort={column.getCanSort()}
            isSorted={column.getIsSorted()}
            context={getContext()}
            onResize={onResize}
          />)}
        </TableRow>
      </SortableContext>
    </DndContext>)}
  </TableHeader>
}

function useHeaderReorder() {
  const { columns, columnOrder, setColumnOrder, pinnedColumns } = useGrid()
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return
    }

    const configurable = (columns || []).filter(isColumnConfigurable).map(columnKey)
    const sameGroup = pinnedColumns.includes(active.id) === pinnedColumns.includes(over.id)
    if (!configurable.includes(active.id) || !configurable.includes(over.id) || !sameGroup) {
      return
    }

    const order = resolveColumnOrder(columnOrder, configurable)
    setColumnOrder(arrayMove(order, order.indexOf(active.id), order.indexOf(over.id)))
  }

  return { sensors, handleDragEnd }
}

function HeaderCheckbox({ table }) {
  const { rows } = table.getRowModel()
  const rowIds = rows.map(({ id, original }) => `${original?.id ?? id}`)
  const { rowsSelected, setRowsSelected } = useGrid()
  const bulkActions = useGridBulkActions()
  const allRowsSelected = rowIds.length > 0 && rowIds.every(id => rowsSelected.includes(id))

  const handleSelectAll = checkedValue => {
    const checked = typeof checkedValue === 'boolean' ? checkedValue : !!checkedValue?.target?.checked

    if (checked) {
      setRowsSelected(prevRowsSelected => Array.from(new Set([...prevRowsSelected, ...rowIds])))
      return
    }

    setRowsSelected([])
  }

  if (!bulkActions) {
    return null
  }

  return <TableHead className='w-4'>
    <Checkbox value={allRowsSelected} onChange={handleSelectAll} />
  </TableHead>
}

function Column({ headerIndex, isPlaceholder, context, column, canSort, isSorted, onResize, columnDef }) {
  const { definitions: { enableTableResize = true } } = useGrid()
  if (!enableTableResize) {
    return <StaticColumn
      headerIndex={headerIndex}
      isPlaceholder={isPlaceholder}
      context={context}
      column={column}
      canSort={canSort}
      isSorted={isSorted}
      columnDef={columnDef}
    />
  }

  return <ResizableColumn
    headerIndex={headerIndex}
    isPlaceholder={isPlaceholder}
    context={context}
    column={column}
    canSort={canSort}
    isSorted={isSorted}
    onResize={onResize}
    columnDef={columnDef}
  />
}

function StaticColumn({ headerIndex, isPlaceholder, context, column, canSort, isSorted, columnDef }) {
  const { header, className } = columnDef
  const pinnedCell = usePinnedHeaderCell(headerIndex)

  if (isPlaceholder) {
    return null
  }

  return <ColumnHeader
    className={className}
    headClassName={pinnedCell.className}
    style={pinnedCell.style}
    pinned={pinnedCell.pinned}
    column={column}
    columnDef={columnDef}
    isSorted={isSorted}
    canSort={canSort}
    enableColumnResize={false}
  >
    {flexRender(header, context)}
  </ColumnHeader>
}

function ResizableColumn({ headerIndex, isPlaceholder, context, column, canSort, isSorted, onResize, columnDef }) {
  const { activeResizeColumn, resizedColumns } = useTableResizeContext()
  const columnWidth = useColumnWidth({ columnIndex: headerIndex })
  const { header, className, enableColumnResize = true } = columnDef
  const canBreakHeaderText = activeResizeColumn === headerIndex || resizedColumns.includes(headerIndex)
  const pinnedCell = usePinnedHeaderCell(headerIndex)

  if (isPlaceholder) {
    return null
  }

  return <ColumnHeader
    className={className}
    headClassName={pinnedCell.className}
    pinned={pinnedCell.pinned}
    column={column}
    columnDef={columnDef}
    isSorted={isSorted}
    canSort={canSort}
    style={{ ...columnWidth, ...pinnedCell.style }}
    isResizing={activeResizeColumn === headerIndex}
    canBreakHeaderText={canBreakHeaderText}
    enableColumnResize={enableColumnResize}
    onStartResize={event => onResize?.({ columnIndex: headerIndex, event })}
  >
    {flexRender(header, context)}
  </ColumnHeader>
}

function ColumnHeader({ children, className, headClassName, pinned, column, columnDef, isSorted, canSort, style, onStartResize, isResizing, canBreakHeaderText = false, enableColumnResize }) {
  const { definitions: { enableTableResize = true }, setColumnsConfigOpen, state: { resetSorting } } = useGrid()
  const { togglePin, hide } = useGridColumnActions()
  const configurable = isColumnConfigurable(columnDef)
  const key = columnKey(columnDef)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: key, disabled: !configurable })

  const handleToggleSort = () => {
    if (!isSorted) {
      column.toggleSorting(false)
      return
    }

    if (isSorted === 'asc') {
      column.toggleSorting(true)
      return
    }

    resetSorting()
  }

  return <TableHead
    ref={setNodeRef}
    {...attributes}
    {...listeners}
    className={cn(
      'relative group whitespace-nowrap',
      canBreakHeaderText && 'whitespace-normal break-all',
      className,
      headClassName,
      configurable && 'cursor-grab',
      isDragging && 'cursor-grabbing z-40 opacity-80',
    )}
    style={{ ...style, transform: CSS.Translate.toString(transform), transition }}>
    <ColumnResizer
      canResize={enableColumnResize && enableTableResize}
      isResizing={isResizing}
      onStartResize={onStartResize}
    />
    <div className={cn('flex gap-1 items-center', className)}>
      {children}
      {canSort && <SortToggle direction={isSorted || null} onClick={handleToggleSort} />}
      <ColumnHeaderMenu
        pinned={pinned}
        sortDirection={isSorted || null}
        onSortAsc={canSort ? () => isSorted === 'asc' ? resetSorting() : column.toggleSorting(false) : undefined}
        onSortDesc={canSort ? () => isSorted === 'desc' ? resetSorting() : column.toggleSorting(true) : undefined}
        onTogglePin={configurable ? () => togglePin(key) : undefined}
        onHide={configurable ? () => hide(key) : undefined}
        onOpenAllColumns={() => setColumnsConfigOpen(true)}
      />
    </div>
  </TableHead>
}

function ColumnResizer({ canResize, onStartResize, isResizing }) {
  if (!canResize) {
    return null
  }

  return <div
    onMouseDown={onStartResize}
    className={cn(
      'absolute top-0 h-full w-[8px] cursor-col-resize select-none touch-none',
      isResizing ? 'twy-bg-accent opacity-100' : 'twy-subtle-neutral opacity-0 group-hover:opacity-100',
      'right-0',
    )}
  />
}
